"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
const assets = [{
  id: "asset-1",
  badge: "CURRENT PORTFOLIO",
  title: "U.S. Brownfield Data Center Portfolio",
  description: "Six-asset net-leased portfolio providing immediate cash-yield across diversified U.S. markets with institutional tenants.",
  stat1Label: "CAPACITY",
  stat1Value: "36MW",
  stat2Label: "EXPANDABLE CAPACITY",
  stat2Value: "177 MW",
  feature1: "2N REDUNDANCY",
  feature2: "8.3 YR WAULT",
  feature3: "ABSOLUTE NNN",
  ctaText: "View Portfolio Details",
  ctaLink: "/PortfolioPage"
}, {
  id: "asset-2",
  badge: "PIPELINE PHASE I",
  title: "Mexico GB300 Neo Cloud Campus",
  description: "LATAM's first certified GB300 Neo Cloud facility, designed for high-density sovereign compute requirements.",
  stat1Label: "TARGET CAPACITY",
  stat1Value: "40-120 MW",
  stat2Label: "TARGET PUE",
  stat2Value: "1.12",
  feature1: "PRE-DEVELOPMENT",
  feature2: "2027 DELIVERY",
  feature3: "LIQUID COOLED",
  ctaText: "View Mexico Thesis",
  ctaLink: "/TheThesisPage"
}, {
  id: "asset-3",
  badge: "PIPELINE GLOBAL",
  title: "India AI Campus Program",
  description: "Energy-secured sovereign and hyperscaler compute campus featuring modular phasing and extreme rack density.",
  stat1Label: "TARGET CAPACITY",
  stat1Value: "100–300 MW",
  stat2Label: "RACK DENSITY",
  stat2Value: "150 kW",
  feature1: "SITE SELECTION",
  feature2: "H100 ALIGNED",
  feature3: "MODULAR SCALE",
  ctaText: "India Thesis",
  ctaLink: "/TheThesisPage"
}];
function AssetCard({
  asset
}: {
  asset: (typeof assets)[number];
}) {
  return <article className="group flex flex-col overflow-hidden rounded-2xl bg-[#F5EDD8] border border-[#E7D8B8] shadow-md transition-all duration-300 hover:shadow-xl">
      
      <div className="flex flex-1 flex-col p-8 md:p-10">
        <div className="mb-8">
          
        </div>

        <h3 className="mb-4 font-serif text-3xl leading-tight tracking-tight text-[#1C1917]">
          {asset.title.split(" ").map((word, i) => <span key={i} className={word === "Portfolio" || word === "Campus" ? "text-[#B45309]" : ""}>
              {word}{" "}
            </span>)}
        </h3>

        <p className="mb-10 text-lg leading-relaxed text-[#1C1917]/70">
          {asset.description}
        </p>

        <div className="mb-10 grid grid-cols-2 gap-8 border-y border-[#B45309]/10 py-8">
          <div>
            <div className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-[#B45309]">
              {asset.stat1Label}
            </div>
            <div className="font-serif text-3xl text-[#1C1917]">
              {asset.stat1Value}
            </div>
          </div>

          <div>
            <div className="mb-2 font-mono text-[10px] font-bold tracking-[0.2em] text-[#B45309]">
              {asset.stat2Label}
            </div>
            <div className="font-serif text-3xl text-[#1C1917]">
              {asset.stat2Value}
            </div>
          </div>
        </div>

        <div className="mb-10 flex flex-wrap items-center gap-x-6 gap-y-3">
          <span className="font-mono text-[11px] font-semibold tracking-wider text-[#B45309]">
            {asset.feature1}
          </span>
          <span className="h-4 w-px bg-[#B45309]/30" />
          <span className="font-mono text-[11px] font-semibold tracking-wider text-[#B45309]">
            {asset.feature2}
          </span>
          <span className="h-4 w-px bg-[#B45309]/30" />
          <span className="font-mono text-[11px] font-semibold tracking-wider text-[#B45309]">
            {asset.feature3}
          </span>
        </div>

        <div className="mt-auto">
          
        </div>
      </div>

      
    </article>;
}
export default function InfrastructureAssetsSection() {
  return <section className="relative bg-[#FDFAF3] py-24 md:py-32 overflow-hidden">
      
      {/* Top-left glow shadow */}
      <div className="pointer-events-none absolute top-0 left-0 w-[600px] h-[600px] bg-[#E7D8B8]/50 blur-[100px] rounded-full -translate-x-1/3 -translate-y-1/3" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        
        <div className="mb-20 max-w-3xl">
          <div className="mb-6 flex items-center gap-4">
            <span className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-[#B45309]">
              Strategic Portfolio
            </span>
            <div className="h-px flex-1 bg-[#B45309]/20" />
          </div>

          <h2 className="font-serif text-5xl leading-[1.1] tracking-tight text-[#1C1917] md:text-7xl">
            Infrastructure <span className="text-[#B45309]">Assets</span>
          </h2>

          <p className="mt-6 text-xl text-[#1C1917]/60">
            Deploying institutional capital into the backbone of artificial
            intelligence through high-density data centers and sovereign compute
            campuses.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-3">
          {assets.map(asset => <AssetCard key={asset.id} asset={asset} />)}
        </div>
        <p className="mt-12 max-w-5xl text-xs leading-relaxed text-[#1C1917]/50 font-sans">
          *The infrastructure opportunities presented above are indicative only and subject to change without notice. 
          Asset details, financial projections, capacity targets, and timelines reflect current estimates and may be 
          revised as development progresses. This content does not constitute an offer to sell or a solicitation to 
          invest. Prospective investors should conduct their own due diligence prior to making any investment decisions.
        </p>
      </div>
    </section>;
}